/*
  AJAX Cart Drawer: vanilla JS module.
  Handles drawer open/close, focus trapping, body scroll lock,
  and AJAX Cart operations (adding, updating quantity, removing, rendering).
*/

(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var cartDrawerElement = null;
  var panel = null;
  var emptyState = null;
  var contentState = null;
  var itemsContainer = null;
  var subtotalElement = null;
  var lastOpener = null;
  var isOpen = false;

  // Simple money formatter for Shopify cents to "X,XX PLN"
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    amount = amount.replace('.', ',');
    return amount + ' PLN';
  }

  // Labels for the lines this script draws, in the storefront's language
  // (snippets/cart-drawer.liquid sets them); the Ukrainian is the fallback.
  function i18n(key, fallback) {
    var dict = window.CartDrawerI18n || {};
    return dict[key] || fallback;
  }

  // Line items are rendered by string concatenation into innerHTML, and line
  // item properties come from buyer input (ticket apps collect attendee name,
  // day, etc.), so every interpolated value has to be escaped.
  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Renders visible line item properties (event, day, attendee...). Shopify
  // treats keys starting with "_" as private, and apps use them for internal
  // ids, so those stay hidden.
  function renderProperties(properties) {
    if (!properties) return '';

    var rows = '';
    Object.keys(properties).forEach(function (key) {
      if (!key || key.charAt(0) === '_') return;

      var value = properties[key];
      if (value === null || value === undefined || value === '') return;

      rows +=
        '<li class="cart-item__property">' +
          '<span class="cart-item__property-key">' + escapeHtml(key) + ':</span> ' +
          '<span class="cart-item__property-value">' + escapeHtml(value) + '</span>' +
        '</li>';
    });

    if (!rows) return '';
    return '<ul class="cart-item__properties" role="list">' + rows + '</ul>';
  }

  function init() {
    cartDrawerElement = document.querySelector('[data-cart-drawer]');
    if (!cartDrawerElement) return;

    panel = cartDrawerElement.querySelector('[data-cart-drawer-panel]');
    emptyState = cartDrawerElement.querySelector('[data-cart-drawer-empty]');
    contentState = cartDrawerElement.querySelector('[data-cart-drawer-content]');
    itemsContainer = cartDrawerElement.querySelector('[data-cart-drawer-items]');
    subtotalElement = cartDrawerElement.querySelector('[data-cart-drawer-subtotal]');

    if (!panel) return;

    setupEvents();
    setupInterceptAddForm();
    purgeCheckoutOnlyLines();
  }

  // A ticket, a donation or a subscription is one decision made in a modal or
  // on a landing and paid for straight away; it has no business waiting in the
  // cart. Someone who backs out of that checkout used to find the line still
  // here, next to a T-shirt, and a second attempt stacked on top of it. So on
  // every page load those lines are dropped: the modal re-adds its own before
  // it sends the buyer to checkout, nothing else ever needs them.
  var CHECKOUT_ONLY_TYPES = ['Event', '\u0414\u043e\u043d\u0430\u0442']; // Event, Донат
  var CHECKOUT_ONLY_HANDLES = ['pidpyska-galas'];

  function isCheckoutOnly(item) {
    return CHECKOUT_ONLY_TYPES.indexOf(item.product_type) !== -1
      || CHECKOUT_ONLY_HANDLES.indexOf(item.handle) !== -1;
  }

  function purgeCheckoutOnlyLines() {
    fetchCart()
      .then(function (cart) {
        var updates = {};
        var found = false;
        (cart.items || []).forEach(function (item) {
          if (isCheckoutOnly(item)) {
            updates[item.key] = 0;
            found = true;
          }
        });
        if (!found) return null;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (response) { return response.json(); });
      })
      .then(function (cart) {
        if (!cart) return;
        updateHeaderCartCount(cart.item_count);
        if (cartDrawerElement.classList.contains('is-open')) renderCart(cart);
      })
      .catch(function () { /* a cart we cannot read is left alone */ });
  }

  function setupEvents() {
    // Open click listeners (can be added anywhere on the page)
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-cart-drawer-open]');
      if (opener) {
        event.preventDefault();
        open(opener);
      }
    });

    // Close click listeners
    cartDrawerElement.querySelectorAll('[data-cart-drawer-close]').forEach(function (el) {
      el.addEventListener('click', function (event) {
        event.preventDefault();
        close();
      });
    });

    // Quantity change & Remove listeners (delegated)
    cartDrawerElement.addEventListener('click', function (event) {
      var qtyBtn = event.target.closest('[data-qty-change]');
      if (qtyBtn) {
        handleQuantityChange(qtyBtn);
        return;
      }

      var removeBtn = event.target.closest('[data-cart-item-remove]');
      if (removeBtn) {
        handleRemoveItem(removeBtn);
        return;
      }
    });

    setupCheckoutEmailPrefill();
  }

  // The ticket modal sends its own buyers to checkout with the address they
  // typed. A buyer who abandoned that checkout and came back through this drawer
  // used to arrive with an empty contact field, so the two routes to the same
  // cart disagreed about what we already knew.
  //
  // The address is read from the cart at click time, not from the attribute
  // Liquid rendered: the modal writes it AFTER this page was served, and a
  // back-from-checkout restore replays the page with whatever the attribute held
  // at first render — which is exactly the path this exists for. Prefill only;
  // see the header of ticket-modal.js for why it is never the source of truth.
  // Any failure falls through to submitting the form, so the drawer's checkout
  // button cannot be broken by this.
  //
  // `form.submit()` is not that fallback, though it looks like it. A programmatic
  // submit sends no submit button, so `name="checkout"` never reaches Shopify and
  // POST /cart is read as a plain cart update — which answers with a redirect to
  // /cart. This theme has no cart page (the drawer is the cart), so /cart renders
  // the placeholder template and the buyer lands on «Скоро тут буде…» instead of
  // checkout. Everyone who did not arrive through the ticket modal took that
  // path, because "no ticket email" is exactly what sends us into the catch.
  // The flag has to travel in the body instead.
  function setupCheckoutEmailPrefill() {
    var form = cartDrawerElement.querySelector('[data-cart-checkout-form]');
    if (!form) return;

    function submitToCheckout() {
      if (!form.querySelector('input[name="checkout"]')) {
        var flag = document.createElement('input');
        flag.type = 'hidden';
        flag.name = 'checkout';
        flag.value = '';
        form.appendChild(flag);
      }
      form.dataset.prefillDone = '1';
      form.submit();
    }

    form.addEventListener('submit', function (event) {
      if (form.dataset.prefillDone === '1') return;
      event.preventDefault();

      fetchCart()
        .then(function (cart) {
          var email = ((cart.attributes || {}).ticket_email || '').trim();
          if (!email) throw new Error('no ticket email');
          window.location.href = root() + 'checkout?checkout%5Bemail%5D='
            + encodeURIComponent(email);
        })
        .catch(submitToCheckout);
    });
  }

  // Locale-aware root ('/', '/pl/', …). A hardcoded '/checkout' dropped the
  // prefix that the form's own {{ routes.cart_url }} preserved, so a Polish
  // buyer left the pl storefront on the last click before paying.
  function root() {
    var routes = (window.Shopify || {}).routes || {};
    return routes.root || '/';
  }

  function getFocusable() {
    var nodes = panel.querySelectorAll(FOCUSABLE);
    return Array.prototype.filter.call(nodes, function (el) {
      return !el.hasAttribute('disabled') && el.offsetParent !== null;
    });
  }

  function open(opener) {
    if (isOpen) return;
    isOpen = true;
    lastOpener = opener || null;

    // Fetch fresh cart data before opening to ensure accurate rendering
    fetchCart().then(function (cart) {
      renderCart(cart);

      cartDrawerElement.hidden = false;
      requestAnimationFrame(function () {
        cartDrawerElement.classList.add('is-open');
        cartDrawerElement.setAttribute('aria-hidden', 'false');
      });

      document.documentElement.classList.add('is-cart-drawer-open');
      document.body.classList.add('is-cart-drawer-open');

      var focusable = getFocusable();
      var target = focusable[0] || panel;
      target.focus({ preventScroll: true });

      document.addEventListener('keydown', onKeydown, true);
    });
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;

    cartDrawerElement.classList.remove('is-open');
    cartDrawerElement.setAttribute('aria-hidden', 'true');

    document.documentElement.classList.remove('is-cart-drawer-open');
    document.body.classList.remove('is-cart-drawer-open');

    document.removeEventListener('keydown', onKeydown, true);

    var styles = window.getComputedStyle(panel);
    var duration = parseDuration(styles.transitionDuration);
    var hide = function () {
      if (!isOpen) cartDrawerElement.hidden = true;
    };
    if (duration > 0) {
      setTimeout(hide, duration);
    } else {
      hide();
    }

    if (lastOpener && typeof lastOpener.focus === 'function') {
      lastOpener.focus({ preventScroll: true });
    }
    lastOpener = null;
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key === 'Tab') {
      var focusable = getFocusable();
      if (!focusable.length) {
        event.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || active === panel || !panel.contains(active)) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        }
      } else if (active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }
  }

  function parseDuration(value) {
    if (!value) return 0;
    var parts = value.split(',');
    var max = 0;
    parts.forEach(function (raw) {
      var v = raw.trim();
      var ms = v.endsWith('ms') ? parseFloat(v) : parseFloat(v) * 1000;
      if (!isNaN(ms) && ms > max) max = ms;
    });
    return max;
  }

  // --- AJAX API Interactions ---

  function fetchCart() {
    return fetch('/cart.js')
      .then(function (response) {
        return response.json();
      });
  }

  function handleQuantityChange(button) {
    var itemRow = button.closest('[data-cart-item-id]');
    if (!itemRow) return;

    var itemId = itemRow.getAttribute('data-cart-item-id');
    var input = itemRow.querySelector('[data-qty-input]');
    if (!input) return;

    var currentQty = parseInt(input.value, 10);
    var action = button.getAttribute('data-qty-change');
    var newQty = action === 'plus' ? currentQty + 1 : currentQty - 1;

    if (newQty < 1) return;

    // Show visual loading state
    itemRow.style.opacity = '0.5';

    updateCartItem(itemId, newQty);
  }

  function handleRemoveItem(button) {
    var itemRow = button.closest('[data-cart-item-id]');
    if (!itemRow) return;

    var itemId = itemRow.getAttribute('data-cart-item-id');

    // Show visual loading state
    itemRow.style.opacity = '0.3';

    updateCartItem(itemId, 0);
  }

  function updateCartItem(itemId, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: itemId,
        quantity: quantity
      })
    })
    .then(function (response) {
      return response.json();
    })
    .then(function (cart) {
      renderCart(cart);
      updateHeaderCartCount(cart.item_count);
    })
    .catch(function (error) {
      console.error('Error updating cart:', error);
    });
  }

  // Intercept standard Add-to-Cart form submits
  function setupInterceptAddForm() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      // A form that goes straight to checkout (the ambassador tiers) opts out.
      if (form.hasAttribute('data-cart-drawer-skip')) return;
      if (form.action && form.action.indexOf('/cart/add') !== -1) {
        event.preventDefault();

        var submitButton = form.querySelector('[type="submit"]');
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.classList.add('is-loading');
        }

        var formData = new FormData(form);

        fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Failed to add item to cart');
          }
          return response.json();
        })
        .then(function () {
          // Re-enable submit button
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.classList.remove('is-loading');
          }

          // Fetch cart count, animate badge, then open drawer
          fetchCart().then(function (cart) {
            updateHeaderCartCount(cart.item_count);
            open();
          });
        })
        .catch(function (error) {
          console.error('Error adding to cart:', error);
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.classList.remove('is-loading');
          }
        });
      }
    });
  }

  // Update the cart badge in the site header and trigger the pulse animation
  function updateHeaderCartCount(count) {
    var badge = document.querySelector('[data-cart-count]');
    if (!badge) return;

    badge.textContent = count;
    badge.hidden = count === 0;

    if (count > 0) {
      // Restart pulse animation by toggling the class
      badge.classList.remove('cart-badge--pulse');
      // Force reflow so the browser sees the class removal before re-adding
      void badge.offsetWidth;
      badge.classList.add('cart-badge--pulse');

      // Remove class once animation ends to keep the DOM clean
      badge.addEventListener('animationend', function onEnd() {
        badge.classList.remove('cart-badge--pulse');
        badge.removeEventListener('animationend', onEnd);
      });
    }
  }

  // --- Dynamic Rendering ---

  function renderCart(cart) {
    if (!cartDrawerElement) return;

    if (cart.item_count === 0) {
      emptyState.classList.remove('is-hidden');
      contentState.classList.add('is-hidden');
      return;
    }

    emptyState.classList.add('is-hidden');
    contentState.classList.remove('is-hidden');

    subtotalElement.textContent = formatMoney(cart.total_price);

    var html = '';

    cart.items.forEach(function (item, index) {
      var imageUrl = item.image;
      if (!imageUrl) {
        // Fallback images from pre-resolved liquid URLs
        var handle = item.handle || '';
        var fallbacks = window.CartDrawerFallbacks || {};
        if (handle.indexOf('кубик') !== -1) imageUrl = fallbacks['кубик'];
        else if (handle.indexOf('ґанок') !== -1) imageUrl = fallbacks['ґанок'];
        else if (handle.indexOf('сині') !== -1) imageUrl = fallbacks['сині'];
        else if (handle.indexOf('ґанку') !== -1) imageUrl = fallbacks['ґанку'];
        else if (handle.indexOf('чорна') !== -1) imageUrl = fallbacks['чорна'];
        else if (handle.indexOf('шкарпетки') !== -1) imageUrl = fallbacks['шкарпетки'];
        else if (handle.indexOf('кепка') !== -1) imageUrl = fallbacks['кепка'];
        else if (handle.indexOf('кружка') !== -1) imageUrl = fallbacks['кружка'];
        else if (handle.indexOf('шопер') !== -1) imageUrl = fallbacks['шопер'];
        else imageUrl = fallbacks['default'] || '';
      } else {
        // resize shopify cdn image
        imageUrl = imageUrl.replace(/_(?:width|height|width\d+)\.(jpg|png|webp)/, '_240x240.$1');
        if (imageUrl.indexOf('_240x240') === -1) {
          var dotIdx = imageUrl.lastIndexOf('.');
          imageUrl = imageUrl.substring(0, dotIdx) + '_240x240' + imageUrl.substring(dotIdx);
        }
      }

      var variantTitle = item.variant_title;
      var variantHtml = '';
      if (variantTitle && variantTitle !== 'Default Title') {
        variantHtml = '<p class="cart-item__variant">' + escapeHtml(variantTitle) + '</p>';
      }

      var propertiesHtml = renderProperties(item.properties);

      html += '<div class="cart-item" data-cart-item-id="' + escapeHtml(item.key) + '" data-line="' + (index + 1) + '">' +
        '<div class="cart-item__image-wrapper">' +
          '<img src="' + escapeHtml(imageUrl) + '" alt="' + escapeHtml(item.product_title) + '" class="cart-item__image" loading="lazy" width="120" height="120">' +
        '</div>' +
        '<div class="cart-item__details">' +
          '<p class="cart-item__title">' + escapeHtml(item.product_title) + '</p>' +
          variantHtml +
          propertiesHtml +
          '<div class="cart-item__price-row">' +
            '<span class="cart-item__price">' + formatMoney(item.final_line_price) + '</span>' +
          '</div>' +
          '<div class="cart-item__actions">' +
            '<div class="cart-item__qty-selector">' +
              '<button type="button" class="cart-item__qty-btn" data-qty-change="minus" aria-label="' + escapeHtml(i18n('decrease', 'Зменшити кількість')) + '">' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"></line></svg>' +
              '</button>' +
              '<input type="number" value="' + item.quantity + '" min="1" class="cart-item__qty-input" data-qty-input readonly>' +
              '<button type="button" class="cart-item__qty-btn" data-qty-change="plus" aria-label="' + escapeHtml(i18n('increase', 'Збільшити кількість')) + '">' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>' +
              '</button>' +
            '</div>' +
            '<button type="button" class="cart-item__remove" data-cart-item-remove aria-label="' + escapeHtml(i18n('remove', 'Видалити')) + '">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>' +
            '</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    });

    itemsContainer.innerHTML = html;
  }

  // Expose methods globally for cross-module integrations
  window.CartDrawer = {
    open: open,
    close: close,
    refresh: function () {
      return fetchCart().then(renderCart);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
